import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  useMediaQuery,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from '../contexts/SnackbarContext';
import { fetchAllEvents } from '../services/eventService';
import EventList, { Event } from '../components/EventList';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';
import { BASE_URL } from '../config';

const MyEvents = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, userId } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };

  const fetchUserEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all events and filter for user's events
      const allEvents = await fetchAllEvents();
      
      // Filter events to show only those created by the current user
      const userEvents = allEvents.filter(event => event.creatorId === userId);
      
      setEvents(userEvents);
    } catch (err: any) {
      console.error('Error fetching user events:', err);
      setError('Failed to load your events. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserEvents();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchUserEvents]);

  const handleEventClick = (event: Event) => {
    navigate(`/events/${event.id}`);
  };

  const handleCreateEvent = () => {
    navigate('/create-event');
  };

  if (!isAuthenticated) {
    return (
      <Box width="100vw" overflow="hidden">
        <AppMenu 
          onLoginClick={() => setIsLoginOpen(true)}
          onSignupClick={() => setIsSignupOpen(true)}
          currentPath={location.pathname}
        />
        
        <Container maxWidth="lg">
          <Box sx={{ p: 3, textAlign: 'center', mt: 4 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              Please log in to view your events
            </Typography>
            <Button
              variant="contained"
              onClick={() => setIsLoginOpen(true)}
              sx={{ mt: 2 }}
            >
              Login
            </Button>
          </Box>
        </Container>

        {/* Authentication Modals */}
        <LoginModal 
          open={isLoginOpen} 
          onClose={() => setIsLoginOpen(false)} 
          onSwitchToSignup={handleSwitchToSignup}
        />
        <SignupModal 
          open={isSignupOpen} 
          onClose={() => setIsSignupOpen(false)} 
          onSwitchToLogin={handleSwitchToLogin}
        />
      </Box>
    );
  }

  return (
    <Box width="100vw" overflow="hidden">
      <AppMenu 
        onLoginClick={() => setIsLoginOpen(true)}
        onSignupClick={() => setIsSignupOpen(true)}
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        <Box sx={{ p: 3 }}>
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' },
              mb: 4,
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 2,
              gap: { xs: 2, sm: 0 }
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Typography 
                variant="h4" 
                component="h1"
                sx={{ 
                  fontWeight: 700,
                  color: 'primary.main',
                  mb: 0.5,
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }
                }}
              >
                My Events
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  maxWidth: { xs: '80%', sm: 'none' }
                }}
              >
                Manage your created events
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateEvent}
              sx={{ 
                textTransform: 'none',
                px: { xs: 3, sm: 2 },
                py: { xs: 1.5, sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                mt: { xs: 1, sm: 0 },
                fontSize: { xs: '0.9rem', sm: '0.875rem' }
              }}
            >
              Create Event
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress size={60} thickness={4} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : events.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No events found
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                You haven't created any events yet. Start by creating your first event!
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateEvent}
                sx={{ mt: 2 }}
              >
                Create Your First Event
              </Button>
            </Paper>
          ) : (
            <EventList 
              events={events}
              onEventClick={handleEventClick}
              baseUrl={BASE_URL}
            />
          )}
        </Box>
      </Container>

      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default MyEvents; 